import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { translations, galleryItems } from "../i18n/translations";

const FILTERS = ["all", "exterior", "interior"];

export default function Gallery() {
  const { lang, t } = useLanguage();
  const tx = translations.gallery;
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const visible =
    filter === "all" ? galleryItems : galleryItems.filter((g) => g.category === filter);

  const next = () => {
    if (active === null) return;
    const idx = visible.findIndex((g) => g.id === active.id);
    setActive(visible[(idx + 1) % visible.length]);
  };
  const prev = () => {
    if (active === null) return;
    const idx = visible.findIndex((g) => g.id === active.id);
    setActive(visible[(idx - 1 + visible.length) % visible.length]);
  };

  const captionFor = (item) => (lang === "es" ? item.captionEs : item.captionEn);

  return (
    <section id="galeria" className="bg-[#F5F0E8] py-24 lg:py-36">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-[1px] w-10 bg-[#9C8456]" />
            <span
              className="text-[#9C8456] text-[11px] tracking-[0.45em] uppercase"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(tx.eyebrow)}
            </span>
            <div className="h-[1px] w-10 bg-[#9C8456]" />
          </div>
          <h2
            className="text-[#2A2A28] text-[34px] sm:text-[48px] lg:text-[58px] leading-[1.08]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            {t(tx.title1)} <em className="italic">{t(tx.title2)}</em>
          </h2>
        </div>

        <div className="flex flex-wrap justify-center gap-2 lg:gap-3 mb-12">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-6 py-2 text-[11px] tracking-[0.3em] uppercase border transition-all duration-300 ${
                filter === f
                  ? "bg-[#2A2A28] text-[#F5F0E8] border-[#2A2A28]"
                  : "bg-transparent text-[#2A2A28] border-[#9C8456]/40 hover:border-[#2A2A28]"
              }`}
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(tx.filters[f])}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-5">
          {visible.map((img, i) => (
            <button
              key={img.id}
              onClick={() => setActive(img)}
              className={`group relative overflow-hidden bg-[#EDE6D7] ${
                i % 5 === 0 ? "row-span-2 aspect-[3/4] lg:aspect-auto lg:h-[640px]" : "aspect-[4/3]"
              }`}
            >
              <img
                src={img.src}
                alt={captionFor(img)}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-3 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <p
                  className="text-white text-[10px] tracking-[0.35em] uppercase"
                  style={{ fontFamily: "'Jost', sans-serif" }}
                >
                  {t(tx.filters[img.category])}
                </p>
                <p
                  className="text-white text-[18px] mt-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  {captionFor(img)}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActive(null);
            }}
            aria-label="Close"
            className="absolute top-6 right-6 text-white/70 hover:text-white"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous"
            className="absolute left-4 lg:left-12 text-white/70 hover:text-white"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next"
            className="absolute right-4 lg:right-12 text-white/70 hover:text-white"
          >
            <ChevronRight className="w-10 h-10" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={active.src}
              alt={captionFor(active)}
              className="w-full max-h-[80vh] object-contain"
            />
            <div className="text-center mt-5">
              <p
                className="text-white/60 text-[10px] tracking-[0.4em] uppercase"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(tx.filters[active.category])}
              </p>
              <p
                className="text-white text-[22px] mt-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
              >
                {captionFor(active)}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
